/**
 * Woofers Phase 1 — Settings → Texting → "Text me when Woofers needs help".
 *
 * Incoming texts are the platform's to handle: a client's text lands in the
 * Inbox and the owner is NOT texted (Johnny 2026-10-26). This phone gets a
 * short alert only when a text needs a person: in Phase 1, a text from a
 * number that is not a client on file (it cannot show in the Inbox). Shown on
 * the Texting card once Twilio is connected (owner only, the card's own
 * gate). Blank = never text the owner.
 *
 * The heading uses the tenant's assistant name (Settings → AI Assistant);
 * if that read fails or no name is set, it falls back to plain words.
 *
 * Frontends go live on merge, the server on a manual deploy. On a server that
 * predates the route (403 route_not_mapped) this renders NOTHING rather than
 * a Retry that can never succeed (the WordingSettingsCard rule).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, Input, Skeleton } from '@ferro/ui';
import { adminMessagingSettingsApi, adminTenantSettingsApi } from '../../lib/api.js';

/** "+13035550101" → "(303) 555-0101" (the Texting card's format). */
function formatUsPhone(e164: string): string {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

type State =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'load_error'; message: string }
  | { kind: 'ready'; current: string | null }
  | { kind: 'saving'; current: string | null }
  | { kind: 'saved'; current: string | null }
  | { kind: 'save_error'; current: string | null; message: string };

export function TextingEscalationForm({ apiUrl }: { apiUrl: string }): JSX.Element | null {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [value, setValue] = useState('');
  const [assistantName, setAssistantName] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const [res, assistant] = await Promise.all([
      adminMessagingSettingsApi.getEscalation(apiUrl),
      adminTenantSettingsApi.getAssistant(apiUrl),
    ]);
    if (res.reason === 'route_not_mapped') {
      setState({ kind: 'unavailable' });
      return;
    }
    if (res.error || !res.data) {
      setState({ kind: 'load_error', message: res.error ?? 'Could not load the alert number.' });
      return;
    }
    setAssistantName(assistant.data?.assistant.name ?? null);
    setValue(res.data.escalationNotifyTo ? formatUsPhone(res.data.escalationNotifyTo) : '');
    setState({ kind: 'ready', current: res.data.escalationNotifyTo });
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = 'current' in state ? state.current : null;

  const handleSave = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setState({ kind: 'saving', current });
      const res = await adminMessagingSettingsApi.setEscalation(apiUrl, value.trim());
      if (res.error || !res.data) {
        setState({
          kind: 'save_error',
          current,
          // A 400 from the Zod boundary carries `details`; say it in the owner's words.
          message: Array.isArray(res.details)
            ? 'Enter a US or Canadian mobile number, or leave it blank.'
            : res.error ?? 'Could not save. Please try again.',
        });
        return;
      }
      setValue(res.data.escalationNotifyTo ? formatUsPhone(res.data.escalationNotifyTo) : '');
      setState({ kind: 'saved', current: res.data.escalationNotifyTo });
    },
    [apiUrl, current, value],
  );

  if (state.kind === 'unavailable') return null;
  if (state.kind === 'loading') {
    return (
      <div className="ferro-settings__card" data-testid="escalation-loading">
        <Skeleton variant="text" lines={2} />
      </div>
    );
  }
  if (state.kind === 'load_error') {
    return (
      <div className="ferro-settings__card" data-testid="escalation-load-error">
        <Alert variant="error">{state.message}</Alert>
        <div className="ferro-settings__actions">
          <Button variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="ferro-settings__form ferro-settings__card" onSubmit={(e) => void handleSave(e)} data-testid="escalation-form">
      <h3 className="ferro-settings__subheading">
        {assistantName ? `Text me when ${assistantName} needs help` : 'Text me when a text needs a person'}
      </h3>
      <p className="ferro-settings__teaching">
        Texts from your clients go to the Inbox, not your phone. You only get a short text here when one needs you, like a
        text from a number that isn&apos;t a client on file.
      </p>
      <Input
        id="escalation-to"
        label="Your mobile number"
        placeholder="e.g. (720) 555-0100"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (state.kind === 'saved' || state.kind === 'save_error') setState({ kind: 'ready', current });
        }}
        helperText={
          current
            ? `Alerts go to ${formatUsPhone(current)}. Leave blank to stop them.`
            : "Not set. Texts from numbers that aren't clients won't reach anyone until you add your mobile."
        }
        error={state.kind === 'save_error' ? state.message : undefined}
        data-testid="escalation-to"
      />
      {state.kind === 'saved' ? (
        <Alert variant="success">
          <span data-testid="escalation-saved">
            {state.current ? `Saved. Alerts go to ${formatUsPhone(state.current)}.` : 'Saved. You will not get text alerts.'}
          </span>
        </Alert>
      ) : null}
      <div className="ferro-settings__actions">
        <Button type="submit" variant="primary" isLoading={state.kind === 'saving'} data-testid="escalation-save">
          Save
        </Button>
      </div>
    </form>
  );
}
